/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/explicit-module-boundary-types, react/no-array-index-key */
import { UploadFile } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Alert,
  Box,
  IconButton,
  Skeleton,
  styled,
  Theme,
  useTheme,
} from "@mui/material";
import kebabCase from "lodash/kebabCase";
import { useState } from "react";
import { Accept, DropzoneState, FileError, useDropzone } from "react-dropzone";
import { Link, Typography } from "@platform/design-system";

import DropzoneFile from "../DropzoneFile/dropzone-file";
import { DropzoneFileItem, DropzoneRejectionFile } from "../../types";
import { validateFileName, validateFileSize } from "../../util";

const DropZoneBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(9, 0),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: theme.spacing(13),
  fontFamily: theme.typography.fontFamily,
  overflow: "hidden",
  position: "relative",
  border: `1px dashed ${theme.palette.dropzone.lineSoft}`,
  cursor: "pointer",
}));

const DropZoneFilesBox = styled(Box)(({ theme }) => ({
  maxHeight: theme.spacing(44),
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export type DropzoneCustomProps = {
  files: DropzoneFileItem[];
  handleUploadFiles?: (acceptedFiles: File[]) => void;
  maxMbSize: number;
  allowDuplicated?: boolean;
  downloadDisabled?: boolean;
  onFileDownload?: (file: DropzoneFileItem) => void;
  onFileDelete?: (file: DropzoneFileItem) => void;
  handleValidation?: VoidFunction;
  onRejectedFiles?: (rejectedFiles: DropzoneRejectionFile[]) => void;
  acceptedFormatFiles: Accept;
  allowedFormatLabel: string;
  isMultiple?: boolean;
  isAllowedToManage?: boolean;
  isLoading?: boolean;
  showFiles?: boolean;
};

const validateFileSizeError = (
  size: number,
  maxMbSize: number,
): FileError | null => {
  try {
    validateFileSize(size, maxMbSize);
    return null;
  } catch {
    return { code: "oversize", message: "File is too large" };
  }
};

const validateDuplicateFileError = (
  name: string,
  files: DropzoneFileItem[],
  allowDuplicated: boolean,
): FileError | null => {
  if (allowDuplicated) return null;

  const isDuplicate = files?.some((file) => file.name === name);
  return isDuplicate
    ? { code: "duplicate", message: "File is duplicated" }
    : null;
};

const validateFileNameError = (name: string): FileError | null => {
  try {
    validateFileName(name);
    return null;
  } catch {
    return {
      code: "invalid-characters",
      message: "File name contains invalid characters",
    };
  }
};

const DropzoneCustom = ({
  files,
  handleUploadFiles,
  maxMbSize,
  allowDuplicated = false,
  downloadDisabled = false,
  onFileDownload,
  onFileDelete,
  onRejectedFiles,
  acceptedFormatFiles,
  allowedFormatLabel,
  isMultiple = false,
  isAllowedToManage = true,
  isLoading = false,
  showFiles = true,
}: DropzoneCustomProps) => {
  const [rejectedFiles, setRejectedFiles] = useState<DropzoneRejectionFile[]>(
    [],
  );
  const handleDefaultValidation = ({
    name,
    size,
  }: {
    name: string;
    size: number;
  }): FileError[] | null => {
    const errors: FileError[] = [
      validateFileSizeError(size, maxMbSize),
      validateDuplicateFileError(name, files, allowDuplicated),
      validateFileNameError(name),
    ].filter((error) => !!error);

    return errors.length ? errors : null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFormatFiles,
    validator: handleDefaultValidation,
    multiple: isMultiple,
    onDrop: (acceptedFiles, fileRejections) => {
      const rejections: DropzoneRejectionFile[] = [
        ...fileRejections.map((fr) => ({
          file: {
            name: fr.file.name,
          },
          errors: fr.errors.map((e) => ({
            code: e.code,
          })),
        })),
      ];
      if (acceptedFiles.length > 0) {
        try {
          validateFileSize(
            acceptedFiles.reduce((acc, file) => acc + file.size, 0),
            maxMbSize,
          );
          handleUploadFiles?.([...acceptedFiles]);
        } catch {
          rejections.push({
            file: {
              name: acceptedFiles.map((file) => file.name).join(", "),
            },
            errors: [
              {
                code: "oversize",
              },
            ],
          });
        }
      }

      if (rejections.length > 0) {
        const tooManyRejection: DropzoneRejectionFile | undefined =
          rejections.find((r) => r.errors[0].code === "too-many-files");
        const newRejectedFiles = tooManyRejection
          ? [tooManyRejection]
          : rejections;
        onRejectedFiles?.(newRejectedFiles);
        setRejectedFiles(newRejectedFiles);
      }
    },
  });

  const handleRemoveRejected = (file: DropzoneRejectionFile["file"]) => {
    const newFiles = rejectedFiles.filter(
      (currFile) => currFile.file.name !== file.name,
    );
    setRejectedFiles(newFiles);
  };

  return (
    <div data-testid="ds-dropzone-custom">
      <RejectedFilesSection
        rejectedFiles={rejectedFiles}
        maxMbSize={maxMbSize}
        allowedFormatLabel={allowedFormatLabel}
        handleRemoveRejected={handleRemoveRejected}
      />
      <ManagementFilesSection
        isAllowedToManage={isAllowedToManage}
        isLoading={isLoading}
        allowedFormatLabel={allowedFormatLabel}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
      />
      <FilesSection
        showFiles={showFiles}
        isLoading={isLoading}
        isAllowedToManage={isAllowedToManage}
        files={files}
        downloadDisabled={downloadDisabled}
        onFileDownload={onFileDownload}
        handleDelete={onFileDelete}
      />
    </div>
  );
};

const RejectedFilesSection = ({
  rejectedFiles,
  maxMbSize,
  allowedFormatLabel,
  handleRemoveRejected,
}: {
  rejectedFiles: DropzoneRejectionFile[];
  maxMbSize: number;
  allowedFormatLabel: string;
  handleRemoveRejected: (file: DropzoneRejectionFile["file"]) => void;
}) => {
  if (rejectedFiles.length === 0) return null;
  return (
    <DropZoneFilesBox
      data-testid="ds-dropzone-custom-rejected-files"
      mt={2}
      sx={(theme: Theme) => ({
        maxHeight: theme.spacing(44),
        overflow: "auto",
      })}
    >
      {rejectedFiles.map((fileRejected, index) => (
        <DropzoneFileRejected
          data-testid={`ds-dropzone-custom-rejected-file-${kebabCase(
            fileRejected.file.name,
          )}`}
          key={index}
          maxMbSize={maxMbSize}
          fileRejected={fileRejected}
          allowedFormatLabel={allowedFormatLabel}
          onRemove={handleRemoveRejected}
        />
      ))}
    </DropZoneFilesBox>
  );
};

const ManagementFilesSection = ({
  isAllowedToManage,
  isLoading,
  allowedFormatLabel,
  getRootProps,
  getInputProps,
}: {
  isAllowedToManage: boolean;
  isLoading: boolean;
  allowedFormatLabel: string;
  getRootProps: DropzoneState["getRootProps"];
  getInputProps: DropzoneState["getInputProps"];
}) => {
  const theme = useTheme();
  if (!isAllowedToManage) return null;
  if (isLoading) return <Skeleton height={150} variant="rectangular" />;
  return (
    <DropZoneBox
      data-testid="ds-dropzone-custom-management"
      {...getRootProps({ mt: theme.spacing(3) })}
    >
      <UploadFileOutlinedIcon color="action" />
      <input {...getInputProps()} />
      <Typography variant="body-large" mt={1}>
        Drag and drop file here or{" "}
        <Link component="span" underline="none">
          <strong>Choose File</strong>
        </Link>
      </Typography>
      <Typography color="textSecondary" mt={0.5}>
        Supported Files: {allowedFormatLabel}
      </Typography>
    </DropZoneBox>
  );
};

const FilesSection = ({
  showFiles,
  isLoading,
  files,
  isAllowedToManage,
  handleDelete,
  downloadDisabled,
  onFileDownload,
}: {
  showFiles: boolean;
  isLoading: boolean;
  isAllowedToManage: boolean;
  downloadDisabled: boolean;
  files: DropzoneFileItem[];
  handleDelete?: (file: DropzoneFileItem) => void;
  onFileDownload?: (file: DropzoneFileItem) => void;
}) => {
  if (!showFiles) return null;
  return (
    <Box
      data-testid="ds-dropzone-custom-files"
      mt={3}
      sx={(theme) => ({
        maxHeight: theme.spacing(44),
        overflow: "auto",
      })}
      display="flex"
      flexDirection="column"
      gap={1.25}
    >
      {isLoading
        ? Array.from({ length: 3 }).map((_, index) => (
            <Box
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              key={`item-${index}`}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box display="flex" flex={1}>
                <Skeleton width={200} height={53} />
              </Box>
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          ))
        : files?.length > 0 &&
          files?.map((file, index) => (
            <DropzoneFile
              data-testid={`ds-dropzone-custom-file-${kebabCase(file.name)}`}
              avatarIcon={<UploadFile color={"primary"} />}
              key={file.name + index}
              file={file}
              icon={
                isAllowedToManage &&
                !isLoading && (
                  <IconButton onClick={() => handleDelete?.(file)}>
                    <DeleteIcon />
                  </IconButton>
                )
              }
              downloadDisabled={downloadDisabled}
              onFileDownload={onFileDownload}
            />
          ))}
    </Box>
  );
};

const DropzoneFileRejected = ({
  fileRejected,
  maxMbSize,
  allowedFormatLabel,
  onRemove,
}: {
  fileRejected: DropzoneRejectionFile;
  maxMbSize: number;
  allowedFormatLabel: string;
  onRemove: (file: { name: string }) => void;
}) => {
  const { errors, file } = fileRejected;
  const error = errors[0];

  const typesUploadErrors: Record<
    string,
    { message: string | React.JSX.Element }
  > = {
    "file-invalid-type": {
      message: ` File extension is not supported (allowed ${allowedFormatLabel})`,
    },
    duplicate: {
      message:
        "You have uploaded a duplicate file. Please make sure you are uploading the correct file or rename the file and try again.",
    },
    oversize: {
      message: `The total size of all uploaded documents cannot exceed (${maxMbSize}MB).`,
    },
    "too-many-files": {
      message: (
        <>
          {" "}
          <Typography variant="caption" component="span" color="inherit">
            <strong>Uploading multiple files is not supported.</strong>
          </Typography>{" "}
          Please upload, assign, and save one file at a time.
        </>
      ),
    },
    "invalid-characters": {
      message: `File names cannot contain special characters ("*/?<>:|).`,
    },
  };

  const errorReason = typesUploadErrors[error.code]?.message;

  return (
    <Alert
      data-testid={`ds-dropzone-custom-rejected-file-${kebabCase(file.name)}`}
      severity="warning"
      sx={{ fontSize: "0.8rem", marginBottom: "8px" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          onClick={() => onRemove(file)}
        >
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      }
    >
      {typeof error.code === "string" ? (
        <>
          <Typography variant="caption" component="span" color="inherit">
            <strong>{file.name}</strong>
          </Typography>{" "}
          could not be uploaded. {errorReason}
        </>
      ) : (
        errorReason
      )}
    </Alert>
  );
};

export default DropzoneCustom;
